import { t } from '@lingui/macro'
import Tooltip from '@mui/material/Tooltip'
import { useResendGreenlightNotification } from '@om1/aspen-api'
import { Send } from 'lucide-react'
import { useEffect, useState } from 'react'
import { ConfirmActionModal } from './ConfirmActionModal'

interface ResendGreenlightNotificationButtonProps {
    // Included for consistency with the other action buttons / future use.
    patientId: string
    mostRecentOrderId?: string
    mostRecentOrderGreenlightId?: string | null
    hasOrders: boolean
}

const ResendGreenlightNotificationButton = ({
    mostRecentOrderId,
    mostRecentOrderGreenlightId,
    hasOrders
}: ResendGreenlightNotificationButtonProps) => {
    const { resendNotification, loading, error } = useResendGreenlightNotification()
    const [isModalOpen, setModalOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const isDisabled = !hasOrders || !mostRecentOrderId || loading

    const tooltip = !hasOrders ? t`No orders exist for this patient` : !mostRecentOrderId ? t`No order with Greenlight ID found` : ''

    const handleConfirm = () => {
        if (!mostRecentOrderId) return
        setSubmitting(true)
        resendNotification(mostRecentOrderId)
    }

    // Close the modal once a submitted resend settles successfully; on error the
    // hook has already shown a snackbar, so keep the modal open for a retry.
    useEffect(() => {
        if (!submitting || loading) return
        setSubmitting(false)
        if (!error) setModalOpen(false)
    }, [submitting, loading, error])

    return (
        <div>
            <Tooltip title={tooltip} arrow>
                <span>
                    <button
                        onClick={() => setModalOpen(true)}
                        disabled={isDisabled}
                        className={`
                            flex items-center justify-center gap-1.5
                            px-3 py-1.5
                            rounded-md
                            transition-colors
                            text-sm font-medium
                            min-w-[140px]
                            shadow-sm
                            h-[36px]
                            ${
                                isDisabled
                                    ? 'bg-gray-400 text-white opacity-50 cursor-not-allowed'
                                    : 'bg-primary-main text-white hover:bg-primary-dark'
                            }
                        `}
                        style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}
                    >
                        <Send size={16} />
                        <span>{t`Resend Notification`}</span>
                    </button>
                </span>
            </Tooltip>

            <ConfirmActionModal
                open={isModalOpen}
                onClose={() => setModalOpen(false)}
                onConfirm={handleConfirm}
                title={t`Resend Notification`}
                message={t`Resend the Greenlight notification email for order ${mostRecentOrderGreenlightId ?? ''}?`}
                confirmLabel={t`Send Notification`}
                cancelLabel={t`Cancel`}
                isLoading={loading}
            />
        </div>
    )
}

export default ResendGreenlightNotificationButton
